import { useCallback, useMemo } from "react";

import type { VoucherNewItem_EditPageQuery } from "@/lib/gql/__generated__/graphql";

import { useTransitionBlock } from "@/hooks/use-transition-block";

import {
	FeatureIslandContainer,
	FeatureIslandHeader,
	FeatureIslandMainWithBackGround,
} from "@/components/features/_layouts";
import { HeaderUpdateButtonAndInSaleSwitch } from "@/components/features/items/edit/_shared/ui/components/header/header-update-button-and-in-sale-switch";

import * as Shared from "../../_shared";
import {
	QuestionDescription,
	QuestionSalesTerm,
	QuestionSellingPrice,
	QuestionVoucherKeyVisual,
	QuestionVoucherName,
	QuestionVoucherPrice,
	QuestionVoucherUsageRestrictions,
} from "../../_shared";
import * as Model from "../model";

type Props = {
	hotelID: string;
	queryData: VoucherNewItem_EditPageQuery;
};

export const MarketingVoucherEditNewPresenter = ({ hotelID, queryData }: Props) => {
	/**
	 * 初期化
	 */
	const {
		viewModel,
		onSetViewModel,
		//
		onVoucherCreate,
	} = Model.UseCase.useInitialized({ hotelID, queryData });

	const { isDirty } = viewModel;
	useTransitionBlock(isDirty);

	/**
	 * 公開
	 */
	const onPublish = useCallback(
		() => onSetViewModel([{ key: "inSale", value: { isEdited: true, value: true } }]),
		[onSetViewModel],
	);
	/**
	 * 非公開
	 */
	const onUnPublish = useCallback(
		() => onSetViewModel([{ key: "inSale", value: { isEdited: true, value: false } }]),
		[onSetViewModel],
	);
	const isValid = useMemo(() => !viewModel.hasError && viewModel.isDirty, [viewModel.hasError, viewModel.isDirty]);

	const handleChangeInSale = useCallback(() => {
		if (viewModel.inSale.value) {
			onUnPublish();
		} else {
			onPublish();
		}
	}, [onUnPublish, onPublish, viewModel.inSale.value]);

	return (
		<Shared.Provider.MarketingVoucherEditContextProvider value={{ viewModel, onSetViewModel }}>
			<FeatureIslandContainer>
				<FeatureIslandHeader
					title="新しい宿泊券の作成"
					rightChildren={
						<HeaderUpdateButtonAndInSaleSwitch
							updateButton={{
								isValid,
								handleClick: onVoucherCreate,
							}}
							inSaleSwitch={{
								inSale: viewModel.inSale,
								handleChangeInSale,
							}}
						/>
					}
				/>

				<FeatureIslandMainWithBackGround islandTitle="基本設定">
					<QuestionVoucherName />

					<QuestionVoucherKeyVisual ownerID={viewModel.hotelID} resourceID={viewModel.voucherID} />

					<QuestionDescription />

					<QuestionVoucherPrice />

					<QuestionSellingPrice />

					<QuestionSalesTerm />

					<QuestionVoucherUsageRestrictions />

					<Shared.QuestionMaxStockNum />
				</FeatureIslandMainWithBackGround>
			</FeatureIslandContainer>
		</Shared.Provider.MarketingVoucherEditContextProvider>
	);
};

MarketingVoucherEditNewPresenter.displayName = "MarketingVoucherEditNewPresenter";
